import { type FetchUserTransactionHistorySummaryByTypeParams } from '@capsicum/common';
import { useCallback, useEffect, useState } from 'react';

import { useInfiniteUserTransactionHistoryQuery } from '../data-access';

import { useTransactionHistoryDayGrouping } from './useTransactionHistoryDayGrouping';

export const useInfiniteTransactionHistory = (params: FetchUserTransactionHistorySummaryByTypeParams) => {
	const { data, fetchNextPage, hasNextPage, isFetching, isPending, refetch } = useInfiniteUserTransactionHistoryQuery(
		{
			limit: 40,
			...params
		}
	);

	const { dataForList, numberOfDates } = useTransactionHistoryDayGrouping({
		data,
		// Because we might not have loaded all the transactions for the last date,
		// so the daily difference would be incorrect
		excludeLastDate: hasNextPage
	});

	const canLoadMorePages = hasNextPage || isFetching || isPending;

	const [targetNumberOfDates, setTargetNumberOfDates] = useState(1);
	const isFetchingNextDate = canLoadMorePages && targetNumberOfDates > numberOfDates;
	const isFetchingFirstDate = isFetchingNextDate && numberOfDates === 0;

	const fetchMoreTransactions = useCallback(() => {
		setTargetNumberOfDates(numberOfDates + 1);

		fetchNextPage({ cancelRefetch: false });
	}, [fetchNextPage, numberOfDates]);

	useEffect(() => {
		if (isFetchingNextDate) {
			fetchNextPage({ cancelRefetch: false });
		}
	}, [
		// It's important to keep `data` in here since some loads don't increase
		// the number of dates
		data,
		fetchNextPage,
		isFetchingNextDate
	]);

	return {
		canLoadMorePages,
		dataForList,
		fetchMoreTransactions,
		isFetchingFirstDate,
		isFetchingNextDate,
		refetch
	};
};
